import { useCallback, useEffect, useMemo, useRef } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { institutionsProgramTypesSelectors } from '@ucheba/store/institutions/program-types'
import { useRouter } from '@ucheba/utils/hooks/useRouter'
import { stringifyUrl } from '@ucheba/utils/helpers/router'
import { EDataKeys } from '@ucheba/utils/types/dataKeys'
import { INavigationProps } from '@ucheba/ui/components/Navigation/types'
import {
  institutionsProgramsSelectors,
  institutionsProgramsThunks,
} from '@ucheba/store/institutions/programs'
import { IInstitutionsPrograms } from '@ucheba/store/institutions/programs/types'
import { capitalizeFirstLetter } from '@ucheba/utils/helpers/string'

interface IUseInstitutionPrograms {
  (props: { id: number }): {
    items: INavigationProps['items']
    activeIndex: number
    onClick?: (_, slug: string) => void
    programs: IInstitutionsPrograms
    programType?: number
    programTypeName: string
  }
}

export const useInstitutionPrograms: IUseInstitutionPrograms = (props) => {
  const { id } = props
  const dispatch = useDispatch()
  const router = useRouter()
  const programTypes = useSelector(institutionsProgramTypesSelectors.programTypes)
  const programs = useSelector(institutionsProgramsSelectors.entity)
  const { query } = useRouter()

  const programType = useMemo(() => {
    if (query[EDataKeys.programTypes]) {
      return { programType: query[EDataKeys.programTypes] }
    }

    if (programTypes && programTypes.length > 0) {
      return { programType: programTypes[0].value }
    }

    return {}
  }, [query, programTypes])

  const getPrograms = useCallback(async () => {
    await dispatch(
      institutionsProgramsThunks.fetch({
        data: {
          ...programType,
          id,
          limit: 2,
        },
      })
    )
  }, [id, dispatch, programType])

  const isRender = useRef(false)

  useEffect(() => {
    if (isRender.current) {
      getPrograms()
    }
    isRender.current = true
  }, [programType, id])

  const items = useMemo(() => {
    return programTypes.map((item) => {
      return {
        slug: String(item.value),
        name: capitalizeFirstLetter(item.name),
      }
    })
  }, [programTypes, router.asPath])

  const onClick = useCallback(
    (_, slug: string) => {
      router.push(
        stringifyUrl(router.asPath, {
          set: {
            [EDataKeys.programTypes]: slug,
          },
        }),
        undefined,
        { shallow: true }
      )
    },
    [router.asPath]
  )

  /** Получаем индекс активного пункта в зависимости от квери параметров */
  const activeIndex = useMemo(() => {
    const activeSection = router.query[EDataKeys.programTypes]

    return activeSection
      ? programTypes.findIndex(({ value }) => String(value) === String(activeSection))
      : 0
  }, [router.query, programTypes])

  const programTypeName = useMemo(() => {
    const programTypesSlug = programTypes?.find(
      ({ value }) => value === Number(programType?.programType)
    )?.slug

    return programTypesSlug === programTypes[0]?.slug ? undefined : programTypesSlug
  }, [programType, programTypes])

  return {
    items,
    activeIndex,
    programs,
    onClick,
    programTypeName,
    programType: Number(programType?.programType),
  }
}
